import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { IngredientCategoryListRow } from "@/modules/inventory/domain/types";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { LayersIcon, PlusIcon } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";
import {
  useDeactivateIngredientCategory,
  useIngredientCategoriesQuery,
  useReorderIngredientCategories,
} from "../api/ingredient-categories-queries";
import {
  IngredientCategoriesFilter,
  type IngredientCategoriesSearch,
} from "../ui/IngredientCategoriesFilter";
import { IngredientCategoriesTable } from "../ui/IngredientCategoriesTable";

function sortRows(
  rows: IngredientCategoryListRow[],
  sortField?: string,
  sortOrder?: "asc" | "desc",
): IngredientCategoryListRow[] {
  if (!sortField) return rows;
  const mult = sortOrder === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const aVal = (a as Record<string, unknown>)[sortField];
    const bVal = (b as Record<string, unknown>)[sortField];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1 * mult;
    if (bVal == null) return -1 * mult;
    if (typeof aVal === "string" && typeof bVal === "string") {
      return aVal.localeCompare(bVal, "lo") * mult;
    }
    if (typeof aVal === "number" && typeof bVal === "number") {
      return (aVal - bVal) * mult;
    }
    return 0;
  });
}

export function IngredientCategoriesPage() {
  const nav = useNavigate({ from: "/app/inventory/ingredient-categories" });
  const search = useSearch({
    from: "/app/inventory/ingredient-categories",
  }) as IngredientCategoriesSearch;

  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;
  const listParams = {
    search: search.search,
    isActive:
      search.isActive === "true"
        ? true
        : search.isActive === "false"
          ? false
          : undefined,
  };

  const list = useIngredientCategoriesQuery(listParams);
  const canManage = useActionPermission(["inventory:create"]);
  const canReorder = useActionPermission(["inventory:update"]);
  const deactivate = useDeactivateIngredientCategory();
  const reorder = useReorderIngredientCategories();

  const sortedAll = useMemo(
    () =>
      sortRows(
        list.data ?? [],
        search.sortField ?? "sortOrder",
        search.sortOrder ?? "asc",
      ),
    [list.data, search.sortField, search.sortOrder],
  );

  const { pageData, totalCount } = useMemo(() => {
    const total = sortedAll.length;
    const page = sortedAll.slice(offset, offset + limit);
    return { pageData: page, totalCount: total };
  }, [sortedAll, offset, limit]);

  const handleReorder = (fromId: string, toId: string) => {
    const ids = sortedAll.map((r) => r.id);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(toId);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const next = [...ids];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved!);
    toast.promise(reorder.mutateAsync(next), {
      loading: "ກຳລັງຈັດລຳດັບ…",
      success: "ຈັດລຳດັບຫມວດສຳເລັດ",
      error: "ຈັດລຳດັບລົ້ມເຫຼວ",
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ຫມວດວັດຖຸດິບ",
              to: "/app/inventory/ingredient-categories",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <LayersIcon className="size-5" />
              ຫມວດວັດຖຸດິບ
            </h2>
            <p className="text-muted-foreground">
              ຈັດກຸ່ມວັດຖຸດິບສຳລັບສູດ ແລະ ການເບິກສຕ໋ອກ
            </p>
          </div>
          {canManage && (
            <Button
              onClick={() =>
                nav({ to: "/app/inventory/ingredient-categories/create" })
              }
            >
              <PlusIcon className="size-4" />
              ສ້າງຫມວດ
            </Button>
          )}
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <IngredientCategoriesFilter />
          <IngredientCategoriesTable
            data={pageData}
            isLoading={list.isLoading}
            offset={offset}
            limit={limit}
            totalCount={totalCount}
            onPaginationChange={(newOffset, newLimit) =>
              nav({
                search: { ...search, offset: newOffset, limit: newLimit },
              })
            }
            sortBy={search.sortField}
            sortOrder={search.sortOrder}
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sortField: id,
                  sortOrder: desc ? "desc" : "asc",
                },
              })
            }
            onEdit={(row) =>
              nav({
                to: "/app/inventory/ingredient-categories/$id/edit",
                params: { id: row.id },
              })
            }
            onDeactivate={async (id) =>
              toast.promise(deactivate.run(id), {
                loading: "ກຳລັງປິດໃຊ້...",
                success: "ປິດໃຊ້ຫມວດສຳເລັດ",
                error: "ປິດໃຊ້ຫມວດລົ້ມເຫຼວ",
              })
            }
            reorderable={!!canReorder}
            onReorder={handleReorder}
          />
        </div>
      </Main>
    </>
  );
}
